import React, { useEffect, useState } from 'react';

import Icon from '../../../components/AppIcon';
import ImageUpload from '../../../components/ui/ImageUpload';
import { storageService } from '../../../services/storageService';

const ImageManager = () => {
	const [images, setImages] = useState([]);
	const [loading, setLoading] = useState(true);
	const [selectedBucket, setSelectedBucket] = useState('images');
	const [uploading, setUploading] = useState(false);

	useEffect(() => {
		loadImages();
	}, [selectedBucket]);

	const loadImages = async () => {
		try {
			setLoading(true);
			const { data, error } = await storageService.listImages(selectedBucket);
			if (error) {
				console.error('Error loading images:', error);
				setImages([]);
			} else {
				// Deduplicate images by name to prevent duplicates
				const uniqueImages = (data || []).reduce((acc, image) => {
					if (!acc.find((img) => img.name === image.name)) {
						acc.push(image);
					}
					return acc;
				}, []);
				setImages(uniqueImages);
			}
		} catch (error) {
			console.error('Error loading images:', error);
			setImages([]);
		} finally {
			setLoading(false);
		}
	};

	const handleUpload = async (file, path = '') => {
		try {
			setUploading(true);
			// The ImageUpload component already handles the actual upload
			// We just need to refresh the list
			alert('Upload successful!');
			loadImages(); // Refresh the list
		} catch (error) {
			console.error('Upload error:', error);
			alert('Upload failed: ' + error.message);
		} finally {
			setUploading(false);
		}
	};

	const handleDelete = async (fileName) => {
		if (!confirm(`Are you sure you want to delete ${fileName}?`)) {
			return;
		}

		try {
			const { error } = await storageService.deleteFile(selectedBucket, fileName);

			if (error) {
				console.error('Delete error:', error);
				alert('Delete failed: ' + error.message);
				return;
			}

			alert('File deleted successfully!');
			loadImages(); // Refresh the list
		} catch (error) {
			console.error('Delete error:', error);
			alert('Delete failed: ' + error.message);
		}
	};

	const copyUrl = (url) => {
		navigator.clipboard.writeText(url);
		alert('URL copied to clipboard!');
	};

	return (
		<div className='space-y-6'>
			{/* Header */}
			<div className='bg-white shadow rounded-lg p-6'>
				<div className='flex items-center justify-between'>
					<div>
						<h1 className='text-2xl font-bold text-gray-900'>Image Manager</h1>
						<p className='text-gray-600'>Upload and manage images for your website</p>
					</div>
					<div className='flex items-center space-x-4'>
						<select
							value={selectedBucket}
							onChange={(e) => setSelectedBucket(e.target.value)}
							className='border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#800020]'
						>
							<option value='images'>Images (Public)</option>
							<option value='documents'>Documents (Private)</option>
						</select>
						<button
							onClick={loadImages}
							className='bg-[#800020] text-white px-4 py-2 rounded-md hover:bg-[#6b001a] transition-colors'
						>
							<Icon name='RefreshCw' className='h-4 w-4 inline mr-2' />
							Refresh
						</button>
					</div>
				</div>
			</div>

			{/* Upload Section */}
			<div className='bg-white shadow rounded-lg p-6'>
				<h2 className='text-lg font-semibold text-gray-900 mb-4'>Upload New Image</h2>
				<ImageUpload onUpload={handleUpload} bucket={selectedBucket} className='mb-4' />
				{uploading && (
					<div className='flex items-center text-[#800020]'>
						<Icon name='Loader' className='h-4 w-4 animate-spin mr-2' />
						Uploading...
					</div>
				)}
			</div>

			{/* Images Grid */}
			<div className='bg-white shadow rounded-lg p-6'>
				<h2 className='text-lg font-semibold text-gray-900 mb-4'>
					Images in {selectedBucket} bucket
				</h2>

				{loading ? (
					<div className='flex items-center justify-center py-8'>
						<Icon name='Loader' className='h-8 w-8 animate-spin text-[#800020]' />
						<span className='ml-2 text-gray-600'>Loading images...</span>
					</div>
				) : images.length === 0 ? (
					<div className='text-center py-8 text-gray-500'>
						<Icon name='Image' className='h-12 w-12 mx-auto mb-4 text-gray-300' />
						<p>No images found in this bucket</p>
					</div>
				) : (
					<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'>
						{images.map((image) => (
							<div key={image.name} className='border border-gray-200 rounded-lg overflow-hidden'>
								<div className='aspect-w-16 aspect-h-12 bg-gray-100'>
									<img
										src={image.url}
										alt={image.name}
										className='w-full h-48 object-cover'
										onError={(e) => {
											e.target.src =
												'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5YTNhZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlPC90ZXh0Pjwvc3ZnPg==';
										}}
									/>
								</div>
								<div className='p-4'>
									<h3 className='font-medium text-gray-900 truncate'>{image.name}</h3>
									<p className='text-sm text-gray-500 mt-1'>
										{image.size ? `${(image.size / 1024).toFixed(1)} KB` : 'Unknown size'}
									</p>
									<div className='mt-3 flex space-x-2'>
										<button
											onClick={() => copyUrl(image.url)}
											className='flex-1 bg-[#DB954E] text-white text-sm px-3 py-1 rounded hover:bg-[#c8853e] transition-colors'
										>
											Copy URL
										</button>
										<button
											onClick={() => handleDelete(image.name)}
											className='flex-1 bg-red-600 text-white text-sm px-3 py-1 rounded hover:bg-red-700 transition-colors'
										>
											Delete
										</button>
									</div>
								</div>
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

export default ImageManager;
